import React, { useState } from 'react';
import { Form as ReactForm, Row } from 'react-bootstrap';
import { Button, Dropdown, SalvBanner, SalvBannerType } from '@salvapp/ui-components';
import AlertAssignmentDropdown from '../AlertAssignmentDropdown';
import './BulkClose.scss';
import { InternalApiAlertType, InternalApiScreeningAlertStatus, InternalApiUserBasicDetails } from '../../api-internal';
import { filterByAlertType_DEPRECATED, toDropDownOptions } from '../../screening/alert-status/alertStatusesHelper';
import { getAlertTypeDropdownOptions } from '../alertTypeHelper';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { bulkCloseApi } from '../../apis';
import { Spinner } from '../../feedback/Spinner';
import { parseErrorMessage } from '../../common/errors/helpers';
import { useMe } from '../../organisation/useMe';

interface Props {
  statuses: InternalApiScreeningAlertStatus[];
}

const cleanupIds = (input: string) => input.replaceAll(/\s/g, '');

const validationSchema = Yup.object().shape({
  alertType: Yup.string().required('Selecting alert type is mandatory'),
  user: Yup.object().required('Select the user to to attach with audit log'),
  newStatus: Yup.string().required('Select the status to set alerts into'),
  comment: Yup.string().required('Add a note to go with the update'),
  alertIds: Yup.string()
    .transform(cleanupIds)
    .matches(/^(\d+,)*\d+$/, 'Failed to find pattern (number, number, ...)')
    .required('Provide alert IDs separated by commas'),
});

const initialValues: BulkCloseFormValues = {
  alertIds: undefined,
  alertType: undefined,
  newStatus: undefined,
  user: undefined,
  comment: 'Bulk closing alerts due to ',
  feedback: undefined,
  feedbackType: SalvBannerType.SUCCESS,
};

export interface BulkCloseFormValues {
  alertType: InternalApiAlertType | undefined;
  user: InternalApiUserBasicDetails | undefined;
  newStatus: string | undefined;
  comment: string | undefined;
  alertIds: string | undefined;
  feedback: string | undefined;
  feedbackType: SalvBannerType;
}

const enum State {
  EDIT,
  FINAL_WARNING,
  LOADING,
}

const BulkCloseForm = ({ statuses }: Props) => {
  const [formStatus, setFormStatus] = useState(State.EDIT);
  const { features } = useMe();

  const submit = (values: BulkCloseFormValues, formikHelpers: FormikHelpers<BulkCloseFormValues>) => {
    setFormStatus(State.LOADING);
    bulkCloseApi
      .bulkUpdate({
        internalApiBulkCloseCommand: {
          alertType: values.alertType!,
          userId: values.user!.id!,
          newStatus: values.newStatus!,
          comment: values.comment!,
          alertIds: values.alertIds!.split(',').map(Number),
        },
      })
      .then(() => {
        formikHelpers.setSubmitting(false);
        formikHelpers.setFieldValue(
          'feedback',
          'Started update in the background, please check individual alerts to verify result'
        );
        formikHelpers.setFieldValue('feedbackType', SalvBannerType.SUCCESS);
        setFormStatus(State.EDIT);
      })
      .catch((e: Error) => {
        formikHelpers.setSubmitting(false);
        formikHelpers.setFieldValue('feedback', parseErrorMessage(e));
        formikHelpers.setFieldValue('feedbackType', SalvBannerType.DANGER);
        setFormStatus(State.EDIT);
      });
  };

  return (
    <Formik initialValues={initialValues} onSubmit={submit} validationSchema={validationSchema}>
      {({
        submitForm,
        values,
        errors,
        isValid,
        isSubmitting,
        setFieldValue,
        handleChange,
        touched,
      }: FormikProps<BulkCloseFormValues>) => {
        return (
          <>
            <div className='m-b-24'>
              <Dropdown
                label='Alert type'
                name='alert-type'
                onChange={(opt: DropdownOption[]) => setFieldValue('alertType', opt[0].value)}
                options={getAlertTypeDropdownOptions(features)}
                placeholder='Please select alert type'
                size='medium'
              />
            </div>
            {values.alertType && (
              <>
                <Row className='ms-0 me-0 row-stretch-children'>
                  <ReactForm.Group controlId='alert-assignee' className='form-group me-4'>
                    <ReactForm.Label>Author</ReactForm.Label>
                    <AlertAssignmentDropdown
                      assignee={values.user}
                      dropdownAlign='start'
                      onChange={(user: InternalApiUserBasicDetails) => setFieldValue('user', user)}
                    />
                  </ReactForm.Group>
                  <div className='me-4'>
                    <Dropdown
                      label='New status'
                      name='alert-status'
                      onChange={(opt: DropdownOption[]) => setFieldValue('newStatus', opt[0].value)}
                      options={toDropDownOptions(filterByAlertType_DEPRECATED(statuses, values.alertType))}
                      placeholder='New alert status:'
                      size='medium'
                      disabled={formStatus != State.EDIT}
                    />
                  </div>
                  <ReactForm.Group className='form-group alert-notes m-b-24' controlId='alert-notes'>
                    <ReactForm.Label>Comment</ReactForm.Label>
                    <ReactForm.Control
                      size='sm'
                      type='text'
                      name='comment'
                      placeholder='A note to go with the update'
                      value={values.comment}
                      onChange={handleChange}
                      isInvalid={touched.comment && !!errors.comment}
                      disabled={formStatus != State.EDIT}
                    />
                  </ReactForm.Group>
                </Row>
                <ReactForm.Group controlId='alert-ids' className='form-group'>
                  <ReactForm.Label>Alert IDs (separated by a &quot;,&quot;)</ReactForm.Label>
                  <ReactForm.Control
                    as='textarea'
                    size='sm'
                    type='text'
                    name='alertIds'
                    placeholder='Alert IDs, separated by a comma'
                    value={values.alertIds}
                    onChange={(e) => {
                      setFieldValue('alertIds', cleanupIds(e.target.value));
                    }}
                    isInvalid={touched.alertIds && !!errors.alertIds}
                    disabled={formStatus != State.EDIT}
                  />
                </ReactForm.Group>
                <Row className='ms-0 me-0'>
                  <span className='flex-grow-1' />
                  {formStatus == State.EDIT && (
                    <Button
                      disabled={isSubmitting || !isValid}
                      onClick={() => setFormStatus(State.FINAL_WARNING)}
                      variant='primary'
                    >
                      Confirm
                    </Button>
                  )}
                  {formStatus == State.EDIT && values.feedback && (
                    <SalvBanner message={values.feedback} type={values.feedbackType} />
                  )}
                  {formStatus == State.LOADING && <Spinner loading={true} />}
                  {formStatus == State.FINAL_WARNING && (
                    <span>
                      <div className='bulk-confirmation'>Confirm status change?</div>
                      <Button onClick={() => setFormStatus(State.EDIT)} variant='outline-primary'>
                        Return to editing
                      </Button>
                      <span className='ms-2 me-2'></span>
                      <Button disabled={isSubmitting || !isValid} onClick={submitForm} variant='danger'>
                        Change {values.alertIds?.split(',').length} alerts
                      </Button>
                    </span>
                  )}
                </Row>
              </>
            )}
          </>
        );
      }}
    </Formik>
  );
};

export default BulkCloseForm;
